import {
    Text,
    Divider,
    Tag,
    ButtonPrimary,
    ButtonLink,
    RowList,
    Row,
    Select,
    Inline,
    Spinner,
    skinVars,
    useWindowSize,
    ButtonFixedFooterLayout,
    IconBillQueriesEuroLight,
    Text3,
    Text5,
} from '@telefonica/mistica';
import { useBillsContext } from '../../../../../context/bills-context/BillsContext';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import {
    getClaimMotives,
    getInvoiceAdditionalConsumptions,
    getInvoiceQuota,
} from '../../../../../services/BillService';
import { claimIconsMap, claimsTag } from '../../../utils/constants/claims';
import { getConsumptionName, getIcon, getUnits } from '../../../utils/functions';
import { formatPrices } from '../../../../../utils/dataTransformation/unitsConversion';
import { useNavigate } from 'react-router-dom';
import React from 'react';
import { getFormatDate } from '../../../../../utils/dataTransformation/dateConversions';
import InfiniteScroll from 'react-infinite-scroll-component';
import './index.scss';
import HeaderComponent from '../../../../../components/HeaderComponent';
import dayjs from 'dayjs';

const ClaimsSelection = () => {
    const navigate = useNavigate();
    const [tagSelected, setTagSelected] = useState('Llamada');
    const { claimType, bill, billClaimsSelected, claims, setClaims, setBillClaimsSelected, setCustomerAccount } =
        useBillsContext();
    const [loadingMotives, setLoadingMotives] = useState<{ [claimId: string]: boolean }>({});
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const size = useWindowSize();
    const [firstLoading, setfirstLoading] = useState(true);

    useEffect(() => {
        if (!bill.id) return;
        getClaimConcepts();
    }, [claimType]);

    useEffect(() => {
        setBillClaimsSelected(new Set());
    }, []);

    const navigationBackHandler = () => navigate('/claims/claim-type');

    const changeClaimSelectionHandler = async (acbrId: string) => {
        const isSelected = billClaimsSelected.has(acbrId);

        setBillClaimsSelected((prevState) => {
            const newSet = new Set(prevState);
            if (isSelected) {
                newSet.delete(acbrId);
            } else {
                newSet.add(acbrId);
            }
            return newSet;
        });

        if (!isSelected) {
            setLoadingMotives((prev) => ({ ...prev, [acbrId]: true }));
            setClaims((prevClaims) =>
                prevClaims.map((claim) =>
                    claim.id === acbrId
                        ? { ...claim, idMotive: '', motive: '', idSubmotive: '', subMotive: '' }
                        : claim,
                ),
            );

            try {
                const response = await getClaimMotives(bill.id, acbrId);
                const tempMotives = response.data.disputedAmounts[0].cpMotives.map(
                    (motive: {
                        motiveId: string;
                        motiveDescription: string;
                        cpSubMotives: Array<{
                            subMotiveDescription: string;
                            subMotiveId: string;
                        }>;
                    }) => ({
                        value: motive.motiveId,
                        text: motive.motiveDescription,
                        submotives: motive.cpSubMotives.map(
                            (sub: { subMotiveDescription: string; subMotiveId: string }) => ({
                                text: sub.subMotiveDescription,
                                value: sub.subMotiveId,
                            }),
                        ),
                    }),
                );
                setClaims((prevClaims) =>
                    prevClaims.map((claim) => (claim.id === acbrId ? { ...claim, motives: tempMotives } : claim)),
                );
                setLoadingMotives((prev) => ({ ...prev, [acbrId]: false }));
            } catch (error) {
                setLoadingMotives((prev) => ({ ...prev, [acbrId]: false }));
                console.error(error);
            }
        }
    };

    const changeMotiveHandler = (value: string, claimId: string, type: 'motive' | 'submotive') => {
        const claimObj = claims.find((c) => c.id === claimId);
        const motives = claimObj?.motives ?? [];
        if (type === 'motive') {
            const selectedMotive = motives.find(
                (motive: { value: string; text: string; submotives: any[] }) => motive.value === value,
            );
            setClaims((prevState) => {
                return prevState.map((claim) => {
                    return claim.id === claimId
                        ? {
                              ...claim,
                              motive: selectedMotive?.text ?? '',
                              idMotive: selectedMotive?.value ?? '',
                              subMotive: '',
                              idSubmotive: '',
                          }
                        : claim;
                });
            });
        } else {
            const motiveObj = motives.find(
                (m: { value: string; text: string; submotives: any[] }) => m.value === claimObj?.idMotive,
            );
            const selectedSubmotive = motiveObj?.submotives.find(
                (sub: { value: string; text: string }) => sub.value === value,
            );
            setClaims((prevState) => {
                return prevState.map((claim) => {
                    return claim.id === claimId
                        ? {
                              ...claim,
                              subMotive: selectedSubmotive?.text ?? '',
                              idSubmotive: selectedSubmotive?.value ?? '',
                          }
                        : claim;
                });
            });
        }
    };

    const setMotivesForAllClaims = () => {
        setClaims((prevState) => {
            return prevState.map((claim) => {
                const claimMotives = claim.motives ?? [];
                const selectedMotive = claimMotives[0];
                const firstSubmotiveObj = selectedMotive?.submotives[0];
                return {
                    ...claim,
                    motive: selectedMotive?.text ?? '',
                    idMotive: selectedMotive?.value ?? '',
                    subMotive: firstSubmotiveObj?.text ?? '',
                    idSubmotive: firstSubmotiveObj?.value ?? '',
                };
            });
        });
    };

    const selectAllHandler = () => {
        if (billClaimsSelected.size > 0) setBillClaimsSelected(new Set());
        else {
            setBillClaimsSelected(
                new Set(
                    claimType === 'additional'
                        ? (tagSelected === 'all'
                              ? claims
                              : claims.filter((claim) => claim.typeDescription === tagSelected)
                          ).map((claim) => claim.id)
                        : claims.map((claim) => claim.id),
                ),
            );
            setMotivesForAllClaims();
        }
    };

    const getClaimConcepts = async () => {
        try {
            const response: AxiosResponse =
                claimType === 'additional'
                    ? await getInvoiceAdditionalConsumptions(bill.id, page, 10)
                    : await getInvoiceQuota(bill.id);

            if (claimType === 'additional') {
                const newClaims = response.data.billOpeningConsumptions[0].appliedCustomerBillingRateSummaryDetails;
                setCustomerAccount(response.data.customerAccount);
                if (page === 1) {
                    setClaims(newClaims);
                } else {
                    setClaims((prevState) => [
                        ...prevState.map((element) => ({ ...element, selected: billClaimsSelected.has(element.id) })),
                        ...newClaims,
                    ]);
                }
                setHasMore(response.data._meta.moreElements);
                setPage((prevPage) => prevPage + 1);
            } else {
                setCustomerAccount(response.data.billOpeningQuotaSummaryItems[0].customerAccount);
                setClaims(response.data.billOpeningQuotaSummaryItems[0].acbrItems);
            }
            setfirstLoading(false);
        } catch (error) {
            setfirstLoading(false);
            console.error(error);
        }
    };

    const getTagName = (tag: string) => {
        switch (tag) {
            case 'Voz':
                return 'callsDetails';
            case 'Msj':
                return 'smsDetails';
            case 'Dat':
                return 'internet';
            case 'Cont':
                return 'contents';
            case 'all':
                return 'all';
            default:
                return tag;
        }
    };

    const selectedClaims = claims.filter((claim) => billClaimsSelected.has(claim.id));
    const isContinueEnabled =
        selectedClaims.length > 0 && selectedClaims.every((claim) => claim.idMotive && claim.idSubmotive);

    const saveHandler = () => {
        navigate('/claims/claim-summary');
    };

    if (firstLoading) {
        return (
            <div className='c-spinner'>
                <Spinner size={24} />
                <span style={{ background: 'white' }} className='c-spinner-overlay'></span>
            </div>
        );
    }

    return (
        <section className='claim-selection'>
            <HeaderComponent
                title={t('openClaim')}
                navigationBarOnBack={navigationBackHandler}
                breadcrumbs={[
                    { title: t('invoice'), url: '/bills' },
                    {
                        title: t('billDetail'),
                        url: `/bills/bill-detail?id=${bill.id}`,
                    },
                    {
                        title: t('billManagement'),
                        url: '/claims/management-invoice',
                    },
                ]}
            />

            {size.width >= 1024 ? (
                <div>
                    <div className='claim-selection__body'>
                        <div className='claim-selection__title'>
                            <Text5>{t('selectBillClaimTitle')}</Text5>
                        </div>
                        <div className='claim-selection__title2'>
                            <Text3 medium color={skinVars.colors.textSecondary}>{`${t('billIssueDate')} ${dayjs(
                                bill?.issueDate,
                            ).format('DD/MM/YYYY')}`}</Text3>
                        </div>
                        <div className='claim-selection__body-divider'>
                            <Divider />
                        </div>
                        {claimType === 'additional' && (
                            <div className='claim-selection__tags'>
                                {claimsTag.map((tag) => (
                                    <div
                                        className='claim-selection__tags-tag'
                                        key={tag}
                                        onClick={() => setTagSelected(tag)}
                                    >
                                        <Tag type={tagSelected === tag ? 'active' : 'inactive'}>
                                            {t(getTagName(tag))}
                                        </Tag>
                                    </div>
                                ))}
                            </div>
                        )}
                        {claimType === 'additional' && (
                            <div className='claim-selection__selection'>
                                <div>
                                    <Text
                                        color={
                                            billClaimsSelected.size >= 300
                                                ? skinVars.colors.controlError
                                                : skinVars.colors.textSecondary
                                        }
                                    >
                                        {`${billClaimsSelected.size} ${
                                            billClaimsSelected.size !== 1
                                                ? t('selected').toUpperCase()
                                                : t('singleSelected').toUpperCase()
                                        }`}
                                    </Text>
                                    {billClaimsSelected.size >= 300 && (
                                        <Text color={skinVars.colors.controlError}>{t('maxSelectionLimit')}</Text>
                                    )}
                                </div>
                                <ButtonLink onPress={selectAllHandler}>
                                    {billClaimsSelected.size > 0 ? t('deselect') : t('selectAll')}
                                </ButtonLink>
                            </div>
                        )}
                        <div className='claim-selection__concepts'>
                            {claimType === 'additional' && (
                                <InfiniteScroll
                                    dataLength={claims.length}
                                    next={getClaimConcepts}
                                    hasMore={hasMore}
                                    loader={
                                        <Inline space={8} alignItems='center'>
                                            <Spinner
                                                size={24}
                                                color={skinVars.colors.navigationBarBackground}
                                            ></Spinner>
                                            {t('loading')}
                                        </Inline>
                                    }
                                >
                                    <RowList>
                                        {(tagSelected === 'all'
                                            ? claims
                                            : claims.filter((claim) => claim.typeDescription === tagSelected)
                                        ).map((claim, index) => {
                                            return (
                                                <React.Fragment key={claim.id}>
                                                    {((index > 0 && claims[index - 1].starDate !== claim.starDate) ||
                                                        index === 0) && (
                                                        <div className='claim-selection__concepts-date'>
                                                            <Text color={skinVars.colors.textSecondary}>
                                                                {getFormatDate(claim.starDate, 'DD MM').toLowerCase()}
                                                            </Text>
                                                        </div>
                                                    )}
                                                    <Row
                                                        title={getConsumptionName(claim)}
                                                        asset={getIcon(
                                                            claim.typeDescription,
                                                            claimIconsMap,
                                                            claim.appliedCustomerBillingRateCharDTOOUTs.find(
                                                                (item: any) => item.name === 'CalledPhone',
                                                            )?.value,
                                                        )}
                                                        description={`${
                                                            claim.appliedCustomerBillingRateCharDTOOUTs.find(
                                                                (item: any) => item.name === 'UsageTime',
                                                            )?.value
                                                        } - ${getUnits(claim)}`}
                                                        detail={formatPrices(claim.amount, 'spent')}
                                                        checkbox={{
                                                            value: billClaimsSelected.has(claim.id),
                                                            onChange: () => changeClaimSelectionHandler(claim.id),
                                                        }}
                                                    />

                                                    {loadingMotives[claim.id] ? (
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                minHeight: 56,
                                                            }}
                                                        >
                                                            <Spinner
                                                                size={24}
                                                                color={skinVars.colors.navigationBarBackground}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className={`claim-selection__claim-reasons ${
                                                                billClaimsSelected.has(claim.id)
                                                                    ? 'claim-selection__claim-reasons-show'
                                                                    : ''
                                                            }`}
                                                        >
                                                            <Select
                                                                label={t('claimSummaryReason')}
                                                                name={t('claimSummaryReason')}
                                                                options={claim.motives ?? []}
                                                                onChangeValue={(value) =>
                                                                    changeMotiveHandler(value, claim.id, 'motive')
                                                                }
                                                                value={claim.idMotive ?? ''}
                                                            />
                                                            <Select
                                                                label={t('claimSummarySecondaryReason')}
                                                                name={t('claimSummarySecondaryReason')}
                                                                options={
                                                                    claim.motives?.find(
                                                                        (motive: {
                                                                            value: string;
                                                                            text: string;
                                                                            submotives: any[];
                                                                        }) => motive.value === claim.idMotive,
                                                                    )?.submotives ?? []
                                                                }
                                                                onChangeValue={(value) =>
                                                                    changeMotiveHandler(value, claim.id, 'submotive')
                                                                }
                                                                value={claim.idSubmotive ?? ''}
                                                                disabled={!claim.idMotive}
                                                            />
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </RowList>
                                </InfiniteScroll>
                            )}
                            {claimType === 'quota' && (
                                <RowList>
                                    {claims.map((claim) => {
                                        return (
                                            <React.Fragment key={claim.id}>
                                                <Row
                                                    title={claim.text}
                                                    asset={<IconBillQueriesEuroLight size={24} />}
                                                    description={`(${getFormatDate(
                                                        claim.startDate,
                                                        'DD M',
                                                    )} a ${getFormatDate(claim.endDate, 'DD M')})`}
                                                    detail={formatPrices(claim.amount, 'spent')}
                                                    checkbox={{
                                                        value: billClaimsSelected.has(claim.id),
                                                        onChange: () => changeClaimSelectionHandler(claim.id),
                                                    }}
                                                />
                                                {loadingMotives[claim.id] ? (
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            minHeight: 56,
                                                        }}
                                                    >
                                                        <Spinner
                                                            size={24}
                                                            color={skinVars.colors.navigationBarBackground}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div
                                                        className={`claim-selection__claim-reasons ${
                                                            billClaimsSelected.has(claim.id)
                                                                ? 'claim-selection__claim-reasons-show'
                                                                : ''
                                                        }`}
                                                    >
                                                        <Select
                                                            label={t('claimSummaryReason')}
                                                            name={t('claimSummaryReason')}
                                                            options={claim.motives ?? []}
                                                            onChangeValue={(value) =>
                                                                changeMotiveHandler(value, claim.id, 'motive')
                                                            }
                                                            value={claim.idMotive ?? ''}
                                                        />
                                                        <Select
                                                            label={t('claimSummarySecondaryReason')}
                                                            name={t('claimSummarySecondaryReason')}
                                                            options={
                                                                claim.motives?.find(
                                                                    (motive: {
                                                                        value: string;
                                                                        text: string;
                                                                        submotives: any[];
                                                                    }) => motive.value === claim.idMotive,
                                                                )?.submotives ?? []
                                                            }
                                                            onChangeValue={(value) =>
                                                                changeMotiveHandler(value, claim.id, 'submotive')
                                                            }
                                                            value={claim.idSubmotive ?? ''}
                                                            disabled={!claim.idMotive}
                                                        />
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </RowList>
                            )}
                        </div>
                    </div>
                    <div className='claim-selection__bottom'>
                        <ButtonPrimary role='onPress' disabled={!isContinueEnabled} onPress={saveHandler}>
                            {t('continue')}
                        </ButtonPrimary>
                    </div>
                </div>
            ) : (
                <ButtonFixedFooterLayout
                    button={
                        <ButtonPrimary role='onPress' disabled={!isContinueEnabled} onPress={saveHandler}>
                            {t('continue')}
                        </ButtonPrimary>
                    }
                >
                    <div className='claim-selection__body'>
                        <div className='claim-selection__title'>
                            <Text5>{t('selectBillClaimTitle')}</Text5>
                        </div>
                        <div className='claim-selection__title2'>
                            <Text3 medium color={skinVars.colors.textSecondary}>{`${t('billIssueDate')} ${dayjs(
                                bill?.issueDate,
                            ).format('DD/MM/YYYY')}`}</Text3>
                        </div>
                        <div className='claim-selection__body-divider'>
                            <Divider />
                        </div>
                        {claimType === 'additional' && (
                            <div className='claim-selection__tags'>
                                {claimsTag.map((tag) => (
                                    <div
                                        className='claim-selection__tags-tag'
                                        key={tag}
                                        onClick={() => setTagSelected(tag)}
                                    >
                                        <Tag type={tagSelected === tag ? 'active' : 'inactive'}>
                                            {t(getTagName(tag))}
                                        </Tag>
                                    </div>
                                ))}
                            </div>
                        )}
                        {claimType === 'additional' && (
                            <div className='claim-selection__selection'>
                                <div>
                                    <Text
                                        color={
                                            billClaimsSelected.size >= 300
                                                ? skinVars.colors.controlError
                                                : skinVars.colors.textSecondary
                                        }
                                    >
                                        {`${billClaimsSelected.size} ${
                                            billClaimsSelected.size !== 1
                                                ? t('selected').toUpperCase()
                                                : t('singleSelected').toUpperCase()
                                        }`}
                                    </Text>
                                    {billClaimsSelected.size >= 300 && (
                                        <Text color={skinVars.colors.controlError}>{t('maxSelectionLimit')}</Text>
                                    )}
                                </div>
                                <ButtonLink onPress={selectAllHandler}>
                                    {billClaimsSelected.size > 0 ? t('deselect') : t('selectAll')}
                                </ButtonLink>
                            </div>
                        )}
                        <div className='claim-selection__concepts'>
                            {claimType === 'additional' && (
                                <InfiniteScroll
                                    dataLength={claims.length}
                                    next={getClaimConcepts}
                                    hasMore={hasMore}
                                    loader={
                                        <Inline space={8} alignItems='center'>
                                            <Spinner
                                                size={24}
                                                color={skinVars.colors.navigationBarBackground}
                                            ></Spinner>
                                            {t('loading')}
                                        </Inline>
                                    }
                                >
                                    <RowList>
                                        {(tagSelected === 'all'
                                            ? claims
                                            : claims.filter((claim) => claim.typeDescription === tagSelected)
                                        ).map((claim, index) => {
                                            return (
                                                <React.Fragment key={claim.id}>
                                                    {((index > 0 && claims[index - 1].starDate !== claim.starDate) ||
                                                        index === 0) && (
                                                        <div className='claim-selection__concepts-date'>
                                                            <Text color={skinVars.colors.textSecondary}>
                                                                {dayjs(claim.starDate).locale('es').format('D MMMM')}
                                                            </Text>
                                                        </div>
                                                    )}
                                                    <Row
                                                        title={getConsumptionName(claim)}
                                                        asset={getIcon(
                                                            claim.typeDescription,
                                                            claimIconsMap,
                                                            claim.appliedCustomerBillingRateCharDTOOUTs.find(
                                                                (item: any) => item.name === 'CalledPhone',
                                                            )?.value,
                                                        )}
                                                        description={`${
                                                            claim.appliedCustomerBillingRateCharDTOOUTs.find(
                                                                (item: any) => item.name === 'UsageTime',
                                                            )?.value
                                                        } - ${getUnits(claim)}`}
                                                        detail={formatPrices(claim.amount, 'spent')}
                                                        checkbox={{
                                                            value: billClaimsSelected.has(claim.id),
                                                            onChange: () => changeClaimSelectionHandler(claim.id),
                                                        }}
                                                    />
                                                    {loadingMotives[claim.id] ? (
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                minHeight: 56,
                                                            }}
                                                        >
                                                            <Spinner
                                                                size={24}
                                                                color={skinVars.colors.navigationBarBackground}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className={`claim-selection__claim-reasons ${
                                                                billClaimsSelected.has(claim.id)
                                                                    ? 'claim-selection__claim-reasons-show'
                                                                    : ''
                                                            }`}
                                                        >
                                                            <Select
                                                                label={t('claimSummaryReason')}
                                                                name={t('claimSummaryReason')}
                                                                options={claim.motives ?? []}
                                                                onChangeValue={(value) =>
                                                                    changeMotiveHandler(value, claim.id, 'motive')
                                                                }
                                                                value={claim.idMotive ?? ''}
                                                            />
                                                            <Select
                                                                label={t('claimSummarySecondaryReason')}
                                                                name={t('claimSummarySecondaryReason')}
                                                                options={
                                                                    claim.motives?.find(
                                                                        (motive: {
                                                                            value: string;
                                                                            text: string;
                                                                            submotives: any[];
                                                                        }) => motive.value === claim.idMotive,
                                                                    )?.submotives ?? []
                                                                }
                                                                onChangeValue={(value) =>
                                                                    changeMotiveHandler(value, claim.id, 'submotive')
                                                                }
                                                                value={claim.idSubmotive ?? ''}
                                                            />
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </RowList>
                                </InfiniteScroll>
                            )}
                            {claimType === 'quota' && (
                                <RowList>
                                    {claims.map((claim) => {
                                        return (
                                            <React.Fragment key={claim.id}>
                                                <Row
                                                    title={claim.text}
                                                    asset={<IconBillQueriesEuroLight size={24} />}
                                                    description={`(${getFormatDate(
                                                        claim.startDate,
                                                        'DD M',
                                                    )} a ${getFormatDate(claim.endDate, 'DD M')})`}
                                                    detail={formatPrices(claim.amount, 'spent')}
                                                    checkbox={{
                                                        value: billClaimsSelected.has(claim.id),
                                                        onChange: () => changeClaimSelectionHandler(claim.id),
                                                    }}
                                                />
                                                {loadingMotives[claim.id] ? (
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            minHeight: 56,
                                                        }}
                                                    >
                                                        <Spinner
                                                            size={24}
                                                            color={skinVars.colors.navigationBarBackground}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div
                                                        className={`claim-selection__claim-reasons ${
                                                            billClaimsSelected.has(claim.id)
                                                                ? 'claim-selection__claim-reasons-show'
                                                                : ''
                                                        }`}
                                                    >
                                                        <Select
                                                            label={t('claimSummaryReason')}
                                                            name={t('claimSummaryReason')}
                                                            options={claim.motives ?? []}
                                                            onChangeValue={(value) =>
                                                                changeMotiveHandler(value, claim.id, 'motive')
                                                            }
                                                            value={claim.idMotive ?? ''}
                                                        />
                                                        <Select
                                                            label={t('claimSummarySecondaryReason')}
                                                            name={t('claimSummarySecondaryReason')}
                                                            options={
                                                                claim.motives?.find(
                                                                    (motive: {
                                                                        value: string;
                                                                        text: string;
                                                                        submotives: any[];
                                                                    }) => motive.value === claim.idMotive,
                                                                )?.submotives ?? []
                                                            }
                                                            onChangeValue={(value) =>
                                                                changeMotiveHandler(value, claim.id, 'submotive')
                                                            }
                                                            value={claim.idSubmotive ?? ''}
                                                            disabled={!claim.idMotive}
                                                        />
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </RowList>
                            )}
                        </div>
                    </div>
                </ButtonFixedFooterLayout>
            )}
        </section>
    );
};

export default ClaimsSelection;
